/**
 * Owns the motif, its paint layer (a Canvas2D raster), undo/redo and the stroke in progress.
 */
import type { Complex } from '../geometry/complex';
import {
  type TriangleGroup,
  insideRotationDomain,
  insideTriangle,
} from '../geometry/triangle-group';
import { StrokeCapture } from '../paint/capture';
import { MotifHistory } from '../paint/history';
import { ERASER, type Motif, type Role, type Symmetry, cloneMotif } from '../paint/motif';
import { MotifRaster, type Paint2D } from '../paint/raster';

export interface Coverage {
  /** [ink, vermilion, gold, ivory, total] mean coverages over the domain. */
  values: [number, number, number, number, number];
}

export class Painter {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private raster!: MotifRaster;
  tg!: TriangleGroup;
  history: MotifHistory;
  private capture: StrokeCapture | null = null;
  /** Incremented whenever the paint layer changes (renderers re-upload when it differs). */
  version = 0;
  coverage: Coverage['values'] = [0, 0, 0, 0, 0];
  private probe: HTMLCanvasElement;
  private readonly maxSide: number;
  onChange: () => void = () => {};

  constructor(initial: Motif, maxSide = 1024) {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) throw new Error('Canvas 2D is not available');
    this.ctx = ctx;
    this.probe = document.createElement('canvas');
    this.history = new MotifHistory(initial);
    this.maxSide = maxSide;
  }

  get motif(): Motif {
    return this.history.current;
  }

  get box() {
    return this.raster.box;
  }

  get painting(): boolean {
    return this.capture !== null;
  }

  setGroup(tg: TriangleGroup): void {
    this.tg = tg;
    this.raster = new MotifRaster(tg, this.ctx as unknown as Paint2D, { maxSide: this.maxSide });
    this.rebuild();
  }

  /** Re-rasterises every stroke (after undo/redo/clear or a change of group). */
  rebuild(): void {
    this.raster.rebuild(this.motif);
    this.changed();
  }

  private changed(): void {
    this.version++;
    this.onChange();
  }

  begin(
    world: Complex,
    role: Role | typeof ERASER,
    size: number,
    symmetry: Symmetry,
    width = 1,
  ): void {
    this.capture = new StrokeCapture(this.tg, {
      role,
      size,
      symmetry,
      onAppend: (piece, first) => {
        this.raster.drawRange(piece, first, piece.points.length - 1);
        this.version++;
      },
    });
    this.capture.add(world, width);
  }

  extend(world: Complex, width = 1): void {
    this.capture?.add(world, width);
  }

  end(): void {
    if (!this.capture) return;
    const pieces = this.capture.finish();
    this.capture = null;
    this.history.add(pieces);
    this.changed();
  }

  /** Abandons the stroke in progress (e.g. a second finger turned it into a gesture). */
  cancel(): void {
    if (!this.capture) return;
    this.capture = null;
    this.rebuild();
  }

  undo(): string | null {
    const r = this.history.undo();
    if (r) this.rebuild();
    return r;
  }

  redo(): string | null {
    const r = this.history.redo();
    if (r) this.rebuild();
    return r;
  }

  replace(m: Motif, label: string): void {
    this.history.replace(cloneMotif(m), label);
    this.rebuild();
  }

  /**
   * Mean role coverage over the domain (F for the reflection group, W for the rotation group),
   * from a small downsampled copy of the paint layer.
   */
  measureCoverage(rotation: boolean): Coverage['values'] {
    const n = 40;
    const box = this.raster.box;
    const bw = box.x1 - box.x0;
    const bh = box.y1 - box.y0;
    const w = bw >= bh ? n : Math.max(8, Math.round((n * bw) / bh));
    const h = bh >= bw ? n : Math.max(8, Math.round((n * bh) / bw));
    this.probe.width = w;
    this.probe.height = h;
    const pctx = this.probe.getContext('2d', { willReadFrequently: true });
    if (!pctx) return this.coverage;
    pctx.clearRect(0, 0, w, h);
    pctx.drawImage(this.canvas, 0, 0, w, h);
    const data = pctx.getImageData(0, 0, w, h).data;
    const sums = [0, 0, 0, 0, 0];
    let count = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const z = { re: box.x0 + ((x + 0.5) / w) * bw, im: box.y0 + ((y + 0.5) / h) * bh };
        const inside = rotation ? insideRotationDomain(this.tg, z) : insideTriangle(this.tg, z);
        if (!inside) continue;
        const i = (y * w + x) * 4;
        const a = data[i + 3] / 255;
        const r = (data[i] / 255) * a;
        const g = (data[i + 1] / 255) * a;
        const b = (data[i + 2] / 255) * a;
        sums[0] += r;
        sums[1] += g;
        sums[2] += b;
        sums[3] += Math.max(0, a - r - g - b);
        sums[4] += a;
        count++;
      }
    }
    this.coverage = sums.map((s) => (count ? s / count : 0)) as Coverage['values'];
    return this.coverage;
  }

  /** ImageData of the paint layer (for the CPU renderer). */
  pixels(): ImageData {
    return this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
  }

  /** A higher-resolution raster of the current motif, for export. */
  renderHighRes(maxSide: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D is not available');
    const r = new MotifRaster(this.tg, ctx as unknown as Paint2D, { maxSide });
    r.rebuild(this.motif);
    return c;
  }
}
